"use client";

import Link from "next/link";
import { ROLE_CONFIG, type SDKClass } from "../../lib/sdk";

const FEATURED_COUNT = 4; // cards shown before "View all in FQN Browser →"

function ClassCard({ cls, lang }: { cls: SDKClass; lang: string }) {
  const sources    = cls.methods.filter((m) => m.role === "source").length;
  const sinks      = cls.methods.filter((m) => m.role === "sink").length;
  const sanitizers = cls.methods.filter((m) => m.role === "sanitizer").length;

  return (
    <Link
      href={`/sdk/${lang}/${cls.category}/${cls.id}`}
      className="group flex flex-col rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all bg-white overflow-hidden"
    >
      <div className="px-4 py-3.5 flex-1">
        <code className="text-sm font-bold text-gray-900 font-mono group-hover:text-green-700 transition-colors block mb-1">
          {cls.name}
        </code>
        {cls.fqns.length > 0 && (
          <div className="text-xs font-mono text-gray-400 truncate mb-2" title={cls.fqns[0]}>
            {cls.fqns[0]}
          </div>
        )}
        <p className="text-xs text-gray-600 line-clamp-1">{cls.description}</p>
      </div>

      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-2 flex-wrap">
        {sources > 0 && (
          <span className="flex items-center gap-1 text-xs text-green-700">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            {sources} {sources === 1 ? "source" : "sources"}
          </span>
        )}
        {sinks > 0 && (
          <span className="flex items-center gap-1 text-xs text-red-700">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            {sinks} {sinks === 1 ? "sink" : "sinks"}
          </span>
        )}
        {sanitizers > 0 && (
          <span className="flex items-center gap-1 text-xs text-blue-700">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            {sanitizers} {sanitizers === 1 ? "sanitizer" : "sanitizers"}
          </span>
        )}
        <span className="ml-auto text-xs text-gray-500">{cls.methods.length} methods</span>
      </div>
    </Link>
  );
}

interface Props {
  catId: string;
  catName: string;
  catDescription: string;
  classes: SDKClass[];
  lang: string;
}

export default function CategorySection({ catId, catName, catDescription, classes, lang }: Props) {
  const featured = classes.slice(0, FEATURED_COUNT);
  const remaining = classes.length - FEATURED_COUNT;
  const hasMore = remaining > 0;

  return (
    <section id={catId} className="scroll-mt-8">
      {/* Category header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-lg font-semibold text-gray-900">{catName}</h2>
            <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              {classes.length}
            </span>
          </div>
          <p className="text-sm text-gray-500">{catDescription}</p>
        </div>

        {/* "View all" link on the right for large categories */}
        {hasMore && (
          <Link
            href={`/sdk/${lang}/${catId}`}
            className="flex-shrink-0 text-sm text-gray-400 hover:text-green-600 transition-colors whitespace-nowrap"
          >
            View all {classes.length} →
          </Link>
        )}
      </div>

      {/* Featured class cards — always exactly FEATURED_COUNT */}
      <div className="grid sm:grid-cols-2 gap-3">
        {featured.map((cls) => (
          <ClassCard key={cls.id} cls={cls} lang={lang} />
        ))}
      </div>

      {/* Browse remaining classes — link to category page */}
      {hasMore && (
        <Link
          href={`/sdk/${lang}/${catId}`}
          className="mt-3 flex items-center justify-between w-full px-5 py-3 rounded-xl border border-dashed border-gray-200 hover:border-green-400 hover:bg-green-50/30 transition-all group"
        >
          <div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-green-700">
              Browse {remaining} more {catName} classes
            </span>
            <p className="text-xs text-gray-400 mt-0.5">
              Search, filter by role, and find FQNs in the reference browser
            </p>
          </div>
          <span className="text-gray-300 group-hover:text-green-500 text-lg transition-colors ml-4">
            →
          </span>
        </Link>
      )}
    </section>
  );
}
